import { rgba } from 'polished';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { textStyles } from '../../../theme/config/typography';
import { ButtonMode, ButtonSize } from './enums';
import { StyledButtonProps } from './types';

const ButtonStyles = css<StyledButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: ${({ theme, size = ButtonSize.Big }) =>
    theme.dimensions.button.height[size]};
  border-radius: 0.375rem;
  border: 0;
  padding: ${({ size }) =>
    size === ButtonSize.Big ? '0.625rem 1.25rem' : '0.375rem 1rem'};
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  ${variant({
    variants: {
      ...textStyles,
    },
  })};
  outline: none;
  transition: background-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
  background-color: ${({ theme, mode = ButtonMode.Primary }) =>
    theme.colors.button[mode].background};
  color: ${({ theme, mode = ButtonMode.Primary }) =>
    theme.colors.button[mode].color};
  cursor: pointer;

  ${({ mode = ButtonMode.Primary, theme }) =>
    mode === ButtonMode.Secondary
      ? css`
          border: 1px solid ${theme.colors.background.medium};
          box-shadow: none;
          &:hover {
            background-color: ${rgba(theme.colors.accent, 0.06)};
            border-color: ${rgba(theme.colors.accent, 0.4)};
            color: ${theme.colors.accent};
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.small};
          }
          &:active {
            transform: translateY(0);
            background-color: ${rgba(theme.colors.accent, 0.1)};
            box-shadow: none;
          }
        `
      : css`
          box-shadow: ${theme.shadows.small};
          &:hover {
            background-color: ${rgba(theme.colors.button[mode].background, 0.92)};
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.mid};
          }
          &:active {
            transform: translateY(0);
            background-color: ${rgba(theme.colors.button[mode].background, 0.85)};
            box-shadow: ${theme.shadows.small};
          }
        `}

  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.4;
    box-shadow: none;
  }
  svg {
    width: 1em;
    height: 1em;
    font-size: 1.25rem;
    fill: currentColor;
    margin-right: 0.5rem;
  }
`;

export const StyledButton = styled.button<StyledButtonProps>`
  ${({ isFullWidth }) =>
    isFullWidth &&
    css`
      width: 100%;
    `}
  ${ButtonStyles}
`;

export const StyledButtonLink = styled(Link)<StyledButtonProps>`
  ${ButtonStyles}
  text-decoration: none;
`;

export const StyledButtonExternalLink = styled.a<StyledButtonProps>`
  ${ButtonStyles}
  text-decoration: none;
`;

export const StyledButtonClean = styled.button`
  padding: 0;
  border: 0;
  outline: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: inherit;
  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.3;
  }
`;
